import { ItemHeader } from "./item-header";
import { useCradSize } from "@/hooks/use-card-size";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { AssetsToolBar } from "@/components/common/assets-tool-bar";
import { ItemCard } from "./item-card";
import { ItemSkeletonList } from "./item-card-skeleton";
import { agentManager, agentsHooks, assetsKnowledgeHooks, assetsKnowledgeItemHooks } from "@/manager";
import { LoadingOverlay } from "../common/loading-overlay";
import { MaterialEmptyImage } from "../assets-material/assets";
import { Upload } from "../common/upload";
import { ALLOW_FILE_TYPES, MAX_FILE_SIZE } from "./constant";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { TextButton } from "../common/text-button";
import { PlusIcon } from "lucide-react";
import { useCancelSelectOnBlankClick } from "@/hooks/use-cancel-select-on-blank-click";
import { ModalFolderMove } from "./modal-folder-move";
import { useNavigate, useParams } from "react-router-dom";
import { ModalAgentChoose } from "./modal-agent-choose";

export const AssetsKnowledgeItem = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [documentData, setDocumentData] = useState<{
    id: number; name: string;
  }>({ id: null, name: '' })

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [triggerId, setTriggerId] = useState<number | null>(null);

  // 知识库文件夹hook
  const { knowledgeFolders } = assetsKnowledgeHooks.useAssetsKnowledgeData();
  const { renameKnowledge } = assetsKnowledgeHooks.useKnowledgeRename();
  const { createKnowledge, loading: createFolderLoading } = assetsKnowledgeHooks.useKnowledgeAdd();

  // 知识库文件hook
  const { knowledgeItems, currentFolder, loading } = assetsKnowledgeItemHooks.useKnowledgeItemsData();
  const { deleteKnowledgeItem } = assetsKnowledgeItemHooks.useKnowledgeItemDelete();
  const { uploadKnowledgeItem } = assetsKnowledgeItemHooks.useKnowledgeItemUpload();
  const { downloadKnowledgeItem, loading: downloadLoading } = assetsKnowledgeItemHooks.useKnowledgeItemDownload();
  const { updateKnowledgeItemDetail } = assetsKnowledgeItemHooks.useKnowledgeItemUpdate();
  const { copyKnowledgeItem, loading: copyLoading } = assetsKnowledgeItemHooks.useKnowledgeItemCopy();
  const { moveKnowledgeItem, loading: moveLoading } = assetsKnowledgeItemHooks.useKnowledgeItemMove();

  // 智能体hook
  const { filteredSubscribeAgents, showCreation } = agentsHooks.useSubscribeAgentData();

  const { ref, columns } = useCradSize({
    min: 140,
    max: 210,
    gap: 5,
  });

  // 处理选中单个文件夹
  const handleSelected = ({ id, checked }: { id: number; checked: boolean }) => {
    setSelectedIds(prev => {
      if (checked) {
        return [...prev, id];
      }
      return prev.filter(item => item !== id);
    });
  }

  // 处理全选/反选所有文件夹
  const handleSelectedAll = (checked: boolean) => {
    setSelectedIds(prev => {
      if (checked) {
        return knowledgeItems.map(item => item.id);
      }
      return [];
    });
  }

  // 处理取消选择/退出多选模式
  const handleCancelSelect = () => {
    setSelectedIds([]);
  }

  // 批量删除
  const handleBatchDelete = async () => {
    handleCancelSelect();
    const parentIds = knowledgeItems.filter(item => selectedIds.includes(item.id))
      .map(item => item.parentId);
    deleteKnowledgeItem(parentIds);
  }

  // 单独删除
  const handleDelete = async (id: number) => {
    await deleteKnowledgeItem([id]);
  }

  // 处理下载素材压缩包
  const handleDownloadZip = () => {
    handleCancelSelect();
    const parentIds = knowledgeItems.filter(item => selectedIds.includes(item.id))
      .map(item => item.parentId);
    downloadKnowledgeItem(parentIds);
  }

  const handleSingleDownload = (id: number) => {
    downloadKnowledgeItem([id]);
  }

  const handleUpload = async (files: File[]) => {
    await uploadKnowledgeItem(files);
  }

  // 重命名文件夹
  const handleRenameFolder = (newName: string) => {
    if (currentFolder?.id) {
      renameKnowledge({ id: currentFolder?.id, name: newName });
    }
  }

  // 处理详情
  const handleDetail = (url: string) => {
    window.open(url, '_blank');
  }

  // 打开选择智能体弹窗
  // const handleOpenAgentModal = (id: number, name: string) => {
  //   setShowAgentModal(true);
  //   setDocumentData({ id, name });
  // }

  // 跳转创作
  const handleCreation = (noteId: number, noteName: string) => {
    navigate(`/agent/creation/${agentManager.getAgentId()}/`, {
      state: {
        noteId,
        noteName,
      }
    });
  }

  // 处理移动
  const handleOpenMove = (id: number) => {
    setTriggerId(id);
    setShowMoveModal(true);
  }

  // 处理重命名
  const handleRename = async (id: number, name: string) => {
    await updateKnowledgeItemDetail(id, name);
  }

  const handleInvalid = () => {
    toast.default({
      description: t("Please upload JPG, PNG, MP4, MOV files"),
    });
  }

  useEffect(() => {
    handleCancelSelect();
  }, [params.id])

  // 点击空白区域取消选择/退出多选模式
  const cancelSelectOnBlankClick = useCancelSelectOnBlankClick({
    enabled: selectedIds.length > 0,
    // 弹窗打开时阻塞
    blocked: showMoveModal,
    onClick: handleCancelSelect,
  });

  const handleMove = async (folderIds: number[]) => {
    const parentId = knowledgeItems.filter(item => selectedIds.includes(item.id))
      .map(item => item.parentId);
    await moveKnowledgeItem?.(triggerId ? [triggerId] : parentId, folderIds);
    handleCancelSelect();
  }

  const handleCopy = async (folderIds: number[]) => {
    const parentId = knowledgeItems.filter(item => selectedIds.includes(item.id))
      .map(item => item.parentId);
    await copyKnowledgeItem?.(triggerId ? [triggerId] : parentId, folderIds);
    handleCancelSelect();
  }

  return (
    <div
      className="flex flex-1 flex-col relative h-full overflow-hidden"
      {...cancelSelectOnBlankClick}
    >
      <div className="flex flex-1 flex-col h-full overflow-y-auto">
        <ItemHeader
          name={currentFolder?.folderName}
          onUpload={handleUpload}
        />
        <div className="flex-1 h-full px-8">
          <div
            ref={ref}
            className='grid gap-4 pb-24 gap-y-[110px]'
            style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
          >
            {/* <ItemUploadCard onUpload={handleUpload} /> */}
            {loading && <ItemSkeletonList count={12} />}
            {!loading && knowledgeItems.length > 0 && knowledgeItems.map(item => (
              <ItemCard
                key={item.id}
                id={item.id}
                name={item.contentTitle.split('.')[0] || t('assets.knowledge_item.untitled_note')}
                type={item.itemUrl ? item.itemUrl.split('.').pop() || '' : 'md'}
                time={item.createTime}
                content={item.itemContent}
                isSelected={selectedIds.includes(item.id)}
                openSelected={selectedIds.length > 0}
                showCreation={showCreation}
                itemDetail={item.itemDetail}
                onSelected={handleSelected}
                onDelete={() => handleDelete(item.parentId)}
                onDownload={() => handleSingleDownload(item.parentId)}
                onDetail={() => handleDetail(item.itemUrl)}
                // onCreation={() => handleOpenAgentModal(item.parentId, item.contentTitle.split('.')[0])}
                onCreation={() => handleCreation(item.parentId, item.contentTitle.split('.')[0])}
                onMove={() => handleOpenMove(item.parentId)}
                onRename={handleRename}
              />
            ))}
          </div>
          {!loading && !knowledgeItems.length && (
            <div className="absolute inset-0  flex items-center justify-center">
              <div className="flex flex-col items-center">
                <MaterialEmptyImage className="w-[211px] mb-3" />
                <span className="text-[rgba(102,112,133,1)] text-sm mb-4">
                  {t('assets.knowledge_item.empty_tips')}
                </span>
                {/* <Upload
                  multiple
                  acceptedFileTypes={ALLOW_FILE_TYPES.join(',')}
                  minFileSize={0}
                  maxFileSize={MAX_FILE_SIZE}
                  onUpload={handleUpload}
                  onInvalid={handleInvalid}
                >
                  <TextButton
                    icon={<PlusIcon className="size-5" />}
                    label={t('assets.knowledge_item.upload_file')}
                    className="text-sm font-medium w-[200px]"
                  // loading={createLoading}
                  // disabled={createLoading}
                  />
                </Upload> */}
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={cn(
          "transition-all duration-300 ease-in-out",
          selectedIds.length > 0 ? "opacity-100 -translate-y-6" : "opacity-0 translate-y-6 pointer-events-none"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <AssetsToolBar
          showMove
          showDownload
          onClose={handleCancelSelect}
          isSelectedAll={selectedIds.length === knowledgeItems.length}
          selectedAmount={selectedIds.length}
          onSelectedAll={handleSelectedAll}
          onDelete={handleBatchDelete}
          onDownload={handleDownloadZip}
          onMove={() => setShowMoveModal(true)}
        />
      </div>
      {/* 移动文件夹弹窗 */}
      {showMoveModal && (
        <ModalFolderMove
          open={showMoveModal}
          knowledgeFolders={knowledgeFolders}
          createFolderLoading={createFolderLoading}
          saveLoading={moveLoading || copyLoading}
          onCreateFolder={(name) => createKnowledge({ name })}
          onMove={handleMove}
          onCopy={handleCopy}
          onOpenChange={(open) => {
            setShowMoveModal(open);
            setTriggerId(null);
          }}
        />
      )}
      {/* {showAgentModal && (
        <ModalAgentChoose
          open={showAgentModal}
          onOpenChange={setShowAgentModal}
          onConfirm={handleCreation}
          agents={filteredSubscribeAgents}
        />
      )} */}
      <LoadingOverlay loading={downloadLoading} />
    </div>
  )
}
